import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkle, ShieldCheck, Trash, Power, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Firm-Voice Library — governance surface for saved house-voice examples.
 *
 * Partner-tier only (backend enforces). Lets authorised users:
 *   • Filter by scope / status / source_kind
 *   • Disable or re-enable an example (soft "forget")
 *   • Reclassify firm-wide ↔ engagement-specific
 *   • Hard delete
 *
 * Nothing on this page alters canonical firm language or methodology.
 */
export default function VoiceLibrary() {
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("all");
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (scope !== "all") params.scope = scope;
    if (status !== "all") params.status = status;
    if (source !== "all") params.source_kind = source;
    api.get("/voice/examples", { params })
      .then((r) => setItems(r.data?.examples || []))
      .catch((e) => {
        if (e?.response?.status === 403) {
          toast.error("Voice library is restricted to partner-tier users");
        } else {
          toast.error("Could not load voice library");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [scope, status, source]);

  const patch = async (id, patch) => {
    setBusyId(id);
    try {
      await api.patch(`/voice/examples/${id}`, patch);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this example permanently? Institutional forget — no undo.")) return;
    setBusyId(id);
    try {
      await api.delete(`/voice/examples/${id}`);
      toast.success("Example removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusyId(null); }
  };

  return (
    <PageShell size="standard" testid="voice-library-page">
      <div className="mb-6">
        <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-eyebrow flex items-center gap-1.5">
          <Sparkle size={12} weight="fill" /> Governance
        </div>
        <h1 className="mt-2 font-display text-2xl md:text-4xl font-bold tracking-tight">Firm-Voice Library</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          Explicitly approved partner edits, saved as advisory context for YadidAi.
          <span className="block mt-1 italic" style={{ fontFamily: "Georgia, serif" }}>
            These examples inform tone and phrasing — they do not alter canonical firm language or methodology.
          </span>
        </p>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Scope</div>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="rounded-none w-56 bg-card" data-testid="filter-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All scopes</SelectItem>
              <SelectItem value="firm_wide">Firm-wide (feeds AI)</SelectItem>
              <SelectItem value="engagement_specific">Engagement-specific (not fed to AI)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Status</div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-none w-40 bg-card" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Source</div>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="rounded-none w-44 bg-card" data-testid="filter-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any source</SelectItem>
              <SelectItem value="closeout">Closeout memo</SelectItem>
              <SelectItem value="diagnostic">Diagnostic memo</SelectItem>
              <SelectItem value="brief">Morning brief</SelectItem>
              <SelectItem value="digest">Digest</SelectItem>
              <SelectItem value="proposal">Proposal</SelectItem>
              <SelectItem value="chat_reply">Chat reply</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="voice-empty">
          No examples match. Save one from any YadidAi draft after your edit is filed.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((ex) => (
            <div key={ex.id} className={`border border-border bg-card p-5 ${ex.status === "disabled" ? "opacity-60" : ""}`} data-testid={`voice-row-${ex.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[0.625rem] font-mono uppercase tracking-widest text-primary border border-primary/40 px-1.5 py-0.5">
                    {ex.scope === "firm_wide" ? "Firm-wide" : "Engagement-specific"}
                  </span>
                  <span className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                    {ex.source_kind} · approved {ex.approved_at?.slice(0, 10)} by {ex.approving_user_name || ex.approving_user_email}
                  </span>
                  {ex.status === "disabled" && (
                    <span className="text-[0.625rem] font-mono uppercase tracking-widest border border-amber-500/40 text-amber-500 px-1.5 py-0.5">Disabled</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={busyId === ex.id}
                          onClick={() => patch(ex.id, { status: ex.status === "active" ? "disabled" : "active" })}
                          className="rounded-none border-primary/40" data-testid={`voice-toggle-${ex.id}`}>
                    <Power size={12} weight="fill" className="mr-1" />
                    {ex.status === "active" ? "Disable" : "Enable"}
                  </Button>
                  {ex.scope === "engagement_specific" ? (
                    <Button size="sm" variant="outline" disabled={busyId === ex.id}
                            onClick={() => patch(ex.id, { scope: "firm_wide" })}
                            className="rounded-none border-primary/40" data-testid={`voice-reclassify-${ex.id}`}>
                      <ArrowsClockwise size={12} className="mr-1" /> → Firm-wide
                    </Button>
                  ) : (
                    ex.engagement_id && (
                      <Button size="sm" variant="outline" disabled={busyId === ex.id}
                              onClick={() => patch(ex.id, { scope: "engagement_specific" })}
                              className="rounded-none border-primary/40" data-testid={`voice-reclassify-${ex.id}`}>
                        <ArrowsClockwise size={12} className="mr-1" /> → Engagement-only
                      </Button>
                    )
                  )}
                  <Button size="sm" variant="outline" disabled={busyId === ex.id}
                          onClick={() => remove(ex.id)}
                          className="rounded-none border-destructive/40 text-destructive hover:bg-destructive/10"
                          data-testid={`voice-delete-${ex.id}`}>
                    <Trash size={12} className="mr-1" /> Delete
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Original draft</div>
                  <pre className="text-xs whitespace-pre-wrap font-sans bg-secondary/30 p-3 border border-border/50">{ex.original_text}</pre>
                </div>
                <div>
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-1 flex items-center gap-1">
                    <ShieldCheck size={12} /> Approved edit
                  </div>
                  <pre className="text-xs whitespace-pre-wrap font-sans bg-primary/5 p-3 border border-primary/30">{ex.approved_text}</pre>
                </div>
              </div>

              {(ex.note || (ex.tags || []).length > 0) && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {ex.note && <span className="text-xs italic text-muted-foreground" style={{ fontFamily: "Georgia, serif" }}>"{ex.note}"</span>}
                  {(ex.tags || []).map((t) => (
                    <span key={t} className="text-[0.625rem] font-mono uppercase tracking-widest border border-border px-1.5 py-0.5 text-muted-foreground">{t}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </PageShell>
  );
}
